import React, { type FC, useState } from 'react';

import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Popover, { PopoverHeader } from '@amzn/meridian/popover';
import Text from '@amzn/meridian/text';
import EmDash from 'src/components/shared/emdash';
import useHover from 'src/hooks/useHover';
import type {
  ModifiedDutyCountrySummary,
  ModifiedDutySummaryResponse,
  ModifiedDutyTotalSummary,
} from 'src/services/associateProfile/types';
import { useAppSelector } from 'src/store/store';
import { grammarJoin } from 'src/utils/helpers';

import ModifiedDutyBreakdownModal from './modifiedDutyBreakdownModal';

const MAX_MODIFIED_DUTY_DAYS = 180;

interface ModifiedDutyProps {
  totalAccommodatedDays?: number;
  summaryResponse?: ModifiedDutySummaryResponse | null;
}

const ModifiedDuty: FC<ModifiedDutyProps> = ({ totalAccommodatedDays, summaryResponse }) => {
  const { featureFlags } = useAppSelector((state) => state.user);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const linkRef = useHover(
    () => setIsPopoverOpen(true),
    () => setIsPopoverOpen(false),
  );

  const closePopover = () => {
    setIsPopoverOpen(false);
  };

  return featureFlags.NEW_MODIFIED_DUTY_EXPERIENCE ? (
    <ModifiedDutyV2
      linkRef={linkRef}
      isPopoverOpen={isPopoverOpen}
      closePopover={closePopover}
      totalSummary={summaryResponse?.total}
      countrySummary={summaryResponse?.countrySummary}
    />
  ) : (
    <ModifiedDutyV1
      linkRef={linkRef}
      value={totalAccommodatedDays}
      isPopoverOpen={isPopoverOpen}
      closePopover={closePopover}
    />
  );
};

interface SharedProps {
  linkRef: React.RefObject<HTMLElement>;
  isPopoverOpen: boolean;
  closePopover: () => void;
}

interface V1Props extends SharedProps {
  value: number | undefined;
}

const ModifiedDutyV1 = ({ linkRef, value, isPopoverOpen, closePopover }: V1Props) => {
  return (
    <>
      <Link ref={linkRef} type="secondary">
        {value ?? <EmDash />}&nbsp;of {MAX_MODIFIED_DUTY_DAYS} days
      </Link>
      {linkRef.current && (
        <Popover
          anchorNode={linkRef.current}
          open={isPopoverOpen}
          position="bottom"
          onClose={closePopover}
          indicatorArrow
          maxWidth="400px"
        >
          <PopoverHeader>Modified Duty Counter</PopoverHeader>
          <Column spacing="400" spacingInset="200">
            <Text type="b300">
              Time in Modified Duty is cumulative between placements occurring at the same time. If the Associate is
              accommodated in two placements (TLD or Modified) only 1 day is accrued.
            </Text>
            <Text type="b300">The ‘days’ in the Modified Duty Counter reset on the rolling calendar year.</Text>
          </Column>
        </Popover>
      )}
    </>
  );
};

interface V2Props extends SharedProps {
  totalSummary?: ModifiedDutyTotalSummary;
  countrySummary?: ModifiedDutyCountrySummary[];
}

const ModifiedDutyV2 = ({ linkRef, isPopoverOpen, closePopover, totalSummary, countrySummary }: V2Props) => {
  const [isBreakdownModalOpen, setIsBreakdownModalOpen] = useState(false);
  const countryNames = countrySummary?.map((c) => c.countryName) ?? [];
  const hasMultipleCountries = countryNames.length > 1;

  const renderValue = () => {
    if (totalSummary?.modifiedDutyDays === undefined) {
      return <EmDash />;
    }

    const countrySuffix = hasMultipleCountries ? ` (across ${grammarJoin(countryNames).join('')})` : '';

    return `${totalSummary.modifiedDutyDays} of ${MAX_MODIFIED_DUTY_DAYS} days${countrySuffix}`;
  };

  return (
    <>
      <Link ref={linkRef} type="secondary" onClick={() => setIsBreakdownModalOpen(true)}>
        {renderValue()}
      </Link>
      {linkRef.current && (
        <Popover
          anchorNode={linkRef.current}
          open={isPopoverOpen}
          position="bottom"
          onClose={closePopover}
          indicatorArrow
          maxWidth="400px"
        >
          <PopoverHeader>Modified Duty Counter</PopoverHeader>
          <Column spacing="400" spacingInset="200">
            <Text type="b300">This is the total number of confirmed modified duty days for this associate.</Text>
            <Text type="b300">
              Time in Modified Duty is cumulative between placements occurring at the same time. If the Associate is
              accommodated in two placements (TLD or Modified) only 1 day is accrued.
            </Text>
            <Text type="b300">
              The &apos;days&apos; in the Modified Duty Counter reset on the rolling calendar year. Click for a more
              detailed breakdown.
            </Text>
          </Column>
        </Popover>
      )}
      <ModifiedDutyBreakdownModal
        isOpen={isBreakdownModalOpen}
        closeModal={() => setIsBreakdownModalOpen(false)}
        countrySummary={countrySummary}
        unknownDutyDays={totalSummary?.unknownDutyDays}
      />
    </>
  );
};

export default ModifiedDuty;
